/** Pure undo history for the image editor: snapshots of the committed operations and the shared selection. */
import {EditOperation} from './editOperations';
import {SelectionMask} from './selectionMask';

/** Maximum number of undo steps kept. */
export const HISTORY_LIMIT = 25;

/** One undoable state. Snapshots share operations and masks by reference, so keeping many is cheap. */
export interface DocumentSnapshot {
   operations: EditOperation[];
   selection: SelectionMask | null;
}

export interface DocumentHistory {
   past: DocumentSnapshot[];
   present: DocumentSnapshot;
}

export enum HistoryActionType {
   Push = 'push',
   Amend = 'amend',
   Undo = 'undo',
}

export type HistoryAction =
   | {type: HistoryActionType.Push; snapshot: DocumentSnapshot}
   | {type: HistoryActionType.Amend; snapshot: DocumentSnapshot}
   | {type: HistoryActionType.Undo};

/** Creates the history of an unedited image without a selection. */
export const createHistory = (): DocumentHistory => ({past: [], present: {operations: [], selection: null}});

/** Push adds an undo step (dropping the oldest beyond HISTORY_LIMIT), Amend replaces the current one, Undo steps back. */
export const historyReducer = (history: DocumentHistory, action: HistoryAction): DocumentHistory => {
   switch (action.type) {
      case HistoryActionType.Push:
         return {past: [...history.past, history.present].slice(-HISTORY_LIMIT), present: action.snapshot};
      case HistoryActionType.Amend:
         return {...history, present: action.snapshot};
      case HistoryActionType.Undo:
         if (history.past.length === 0) {
            return history;
         }
         return {past: history.past.slice(0, -1), present: history.past[history.past.length - 1]};
   }
};
